import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type ThemePreference = 'light' | 'dark'

const STORAGE_KEY = 'academy-theme'

interface ThemeContextValue {
  preference: ThemePreference
  /** Alias of `preference` (kept for call sites; there's no 'system' to resolve). */
  resolved: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/** Dark is the default (most users prefer it); a stored 'light' is respected. */
function readStored(): ThemePreference {
  return 'light' === localStorage.getItem(STORAGE_KEY) ? 'light' : 'dark'
}

/**
 * Theme provider. Light or dark only (the OS-following 'system' option was
 * removed). Persists to localStorage; the API-backed `users.preferred_theme`
 * sync lands with auth.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStored)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', preference)
    localStorage.setItem(STORAGE_KEY, preference)
  }, [preference])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved: preference, setPreference: setPreferenceState }),
    [preference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// Provider + hook are colocated by design; the fast-refresh rule doesn't apply here.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
